import { getCurrentNetwork } from "src/configs/connection-config.js";
import { environment, sidechainIdHex } from "src/configs/constance";
import { ERR_TOP_CENTER, INFO_TOP_CENTER } from "src/hooks/useNotify";
import { resetAccountState, updateAccInfo } from "src/redux/account-slice";
import { updateBridgeChain } from "src/redux/bridge-slice";
import { getAvailableChest } from "src/redux/chest-slice";
import { fetchCollectionsV2, fetchForgingInventory, fetchNFTInventory } from "src/redux/inventory2-slice";
import { getWeb3Sender, setWeb3Sender } from "src/wallet-connection";
import web3ModalSetup from "src/wallet-connection/web3-modal-setup";
import Web3 from "web3";

export let provider;
export let web3Modal = web3ModalSetup();

export function clearEventListeners() {
  if (provider) {
    if (provider.removeAllListeners && typeof provider.removeAllListeners === "function") {
      provider.removeAllListeners("accountsChanged");
      provider.removeAllListeners("chainChanged");
      provider.removeAllListeners("disconnect");
    }
    provider = null;
  }
}

export async function disconnectWallet(dp, enqueueSnackbar, shouldNoti = true) {
  web3Modal.clearCachedProvider();
  if (provider) {
    if (provider.disconnect && typeof provider.disconnect === "function") await provider.disconnect();
    dp(resetAccountState());
    setWeb3Sender(null);
    clearEventListeners();
    if (shouldNoti) enqueueSnackbar("Disconnected", INFO_TOP_CENTER);
  }
}

export async function switchNetwork(enqueueSnackbar) {
  const targetNetwork = getCurrentNetwork();

  let web3 = new Web3(provider);
  const currentMetamaskChainId = await web3.eth.getChainId();
  if (currentMetamaskChainId === Number(targetNetwork.chainId)) return;
  if (typeof provider?.wc === "object") throw new Error(`Please switch to ${targetNetwork.chainName}, then reconnect!`);
  try {
    await provider.request({ method: "wallet_switchEthereumChain", params: [{ chainId: targetNetwork.chainId }] });
  } catch (error) {
    console.error(error);
    if (error.code == 4902) {
      try {
        await provider.request({ method: "wallet_addEthereumChain", params: [targetNetwork] });
      } catch (error) {
        enqueueSnackbar(JSON.stringify(error?.message), ERR_TOP_CENTER);
      }
    } else {
      web3Modal.clearCachedProvider();
      if (error?.message == "null") {
        enqueueSnackbar("Reload the page and reconnect now!!", INFO_TOP_CENTER);
        window.location.reload();
      }
    }
  }
}

export function addWalletEvent(dispatch, enqueueSnackbar) {
  if (provider) {
    provider?.on("accountsChanged", async (address) => {
      if (address.length === 0) disconnectWallet(dispatch, enqueueSnackbar);
      else {
        await dispatch(updateAccInfo(address[0], enqueueSnackbar));
        await Promise.all([
          dispatch(getAvailableChest()),
          dispatch(fetchNFTInventory()),
          dispatch(fetchForgingInventory()),
          dispatch(fetchCollectionsV2()),
        ]);
      }
    });
    provider.on("chainChanged", (chainId) => {
      const currentNetwork = getCurrentNetwork();
      if (chainId == sidechainIdHex) dispatch(updateBridgeChain({ srcChain: 0, destChain: 1 }));
      else if (chainId == (environment == "mainnet" ? "0x38" : "0x61"))
        dispatch(updateBridgeChain({ srcChain: 1, destChain: 0 }));
      else if (chainId !== currentNetwork.chainId) {
        enqueueSnackbar(`Please switch to ${currentNetwork.chainName}, then reconnect!`, ERR_TOP_CENTER);
        disconnectWallet(dispatch, enqueueSnackbar);
      }
    });
  }
}

export async function connectWallet(dispatch, enqueueSnackbar) {
  try {
    if (window.ethereum) await window.ethereum.request({ method: "eth_requestAccounts" });
    provider = await web3Modal.connect();
    await switchNetwork(enqueueSnackbar);
    addWalletEvent(dispatch, enqueueSnackbar);
    setWeb3Sender(provider);
    const targetNetwork = getCurrentNetwork();
    const chainId = await getWeb3Sender().eth.getChainId();
    if (chainId != targetNetwork.chainId) {
      throw new Error(`Please change network to ${targetNetwork.chainName} and then reconnect!!`);
    }
    const addresses = await getWeb3Sender().eth.getAccounts();
    await dispatch(updateAccInfo(addresses[0], enqueueSnackbar));
    await Promise.all([
      dispatch(getAvailableChest()),
      dispatch(fetchNFTInventory()),
      dispatch(fetchForgingInventory()),
      dispatch(fetchCollectionsV2()),
    ]);
  } catch (err) {
    console.error(err);
    disconnectWallet(dispatch, enqueueSnackbar, false);
    if (err?.code == 500 || err?.code == -32002)
      enqueueSnackbar("Cannot detect locked wallet, please refresh the page", ERR_TOP_CENTER);
    else {
      enqueueSnackbar(
        (err && (JSON.stringify(err?.message) || JSON.stringify(err).slice(0, 80))) ||
          "Cannot connect wallet! Please check your wallet extension!",
        ERR_TOP_CENTER
      );
    }
  }
}
